import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, ChevronRight, Clock, Sparkles, Send } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import {
  Card, Chip, CompatibilityRing, Input, PageHeader, Button,
  MatchScoreBreakdown, ReputationBadge, LivePulse, Toggle, MatchBadge
} from "../components/Primitives";
import { categories } from "../data/mockData";
import { listRequests } from "../lib/db";
import { computeMatch } from "../data/taxonomy";

const SORTS = [
  { key: "sortNewest", cond: () => true },
  { key: "sortDistance", cond: () => true },
  { key: "sortBudget", cond: () => true },
  { key: "sortResponseTime", cond: () => true },
  { key: "sortReputation", cond: () => true },
  { key: "sortCompat", cond: (analyze) => analyze },
];

export default function Requests() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [analyze, setAnalyze] = useState(false);
  const [sort, setSort] = useState("sortNewest");
  const [category, setCategory] = useState("All");
  const [openedId, setOpenedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ urgentOnly: false, newOnly: false, hasBudget: false });

  useEffect(() => {
    let alive = true;
    listRequests({ excludeOwner: user?.id || null }).then((rows) => {
      if (alive) { setRequests(rows); setLoading(false); }
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [user?.id]);

  const list = useMemo(() => {
    let r = [...requests];
    if (query) {
      const q = query.toLowerCase();
      r = r.filter((x) =>
        (lang === "it" ? x.title : x.titleEn).toLowerCase().includes(q) ||
        (x.material || "").toLowerCase().includes(q) ||
        (x.category || "").toLowerCase().includes(q)
      );
    }
    if (category !== "All") r = r.filter((x) => x.category === category);
    if (filters.urgentOnly) r = r.filter((x) => x.urgent);
    if (filters.newOnly) r = r.filter((x) => x.isNew);
    if (filters.hasBudget) r = r.filter((x) => (x.budgetNum || 0) > 0);
    if (sort === "sortCompat" && analyze) r.sort((a, b) => (b.compatibility ?? -1) - (a.compatibility ?? -1));
    else if (sort === "sortNewest") r.sort((a, b) => a.postedAtMinutes - b.postedAtMinutes);
    else if (sort === "sortDistance") r.sort((a, b) => a.distanceKm - b.distanceKm);
    else if (sort === "sortBudget") r.sort((a, b) => b.budgetNum - a.budgetNum);
    else if (sort === "sortReputation") r.sort((a, b) => b.reputation - a.reputation);
    return r;
  }, [query, sort, category, lang, analyze, requests, filters]);

  const visibleSorts = SORTS.filter((s) => s.cond(analyze));
  const activeFiltersCount =
    (filters.urgentOnly ? 1 : 0) + (filters.newOnly ? 1 : 0) + (filters.hasBudget ? 1 : 0);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto animate-fade-up">
      <PageHeader
        kicker={t("finder.kicker")}
        testId="requests-header"
        title={t("requests.title")}
        subtitle={t("requests.subtitle")}
        right={
          <Button variant="outline" data-testid="requests-filters-btn" onClick={() => setShowFilters((v) => !v)}>
            <SlidersHorizontal className="w-4 h-4" />
            {t("requests.filters")}
            {activeFiltersCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full font-mono text-[10px] font-semibold bg-electric-500 text-ink-900" data-testid="requests-filters-count">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        }
      />

      {showFilters && (
        <Card className="p-4 mb-4" data-testid="requests-filters-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-mono uppercase tracking-widest text-ink-600">{t("requests.filters")}</span>
            <button
              onClick={() => setFilters({ urgentOnly: false, newOnly: false, hasBudget: false })}
              className="text-[11px] text-ink-600 hover:text-ink-900"
              data-testid="requests-filters-reset"
            >
              {t("requests.filtersReset")}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "urgentOnly", label: t("requests.filterUrgent") },
              { key: "newOnly",    label: t("requests.filterNew") },
              { key: "hasBudget",  label: t("requests.filterHasBudget") },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilters((s) => ({ ...s, [f.key]: !s[f.key] }))}
                data-testid={`requests-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${
                  filters[f.key]
                    ? "border-electric-500 bg-electric-500/10 text-electric-600"
                    : "border-carbon-600 text-ink-700 hover:text-ink-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-600" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("requests.search")}
          className="pl-11 py-3.5 text-base"
          data-testid="requests-search-input"
        />
      </div>

      <Card className="p-4 mb-4" data-testid="analyze-toggle-card">
        <Toggle
          checked={analyze}
          onChange={(v) => {
            setAnalyze(v);
            if (!v && sort === "sortCompat") setSort("sortNewest");
          }}
          label={
            <span className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-electric-400" />
              {t("requests.compatibilityToggle")}
            </span>
          }
          help={analyze ? t("matchScore.onHelp") : t("matchScore.offHelp")}
          testId="analyze-toggle"
        />
      </Card>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 -mx-4 px-4 md:mx-0 md:px-0">
        {["All", ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`cat-filter-${c}`}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs border transition ${
              category === c
                ? "border-electric-500 bg-electric-500/10 text-electric-600"
                : "border-carbon-600 text-ink-700 hover:text-ink-900"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-6 text-xs flex-wrap">
        <span className="text-ink-600 font-mono uppercase tracking-wider">{t("requests.sortBy")}</span>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {visibleSorts.map(({ key }) => (
            <button
              key={key}
              onClick={() => setSort(key)}
              data-testid={`sort-${key}`}
              className={`px-2.5 py-1 rounded-md transition whitespace-nowrap ${
                sort === key ? "text-electric-600 bg-electric-500/10" : "text-ink-700 hover:text-ink-900"
              }`}
            >
              {t(`requests.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-ink-500 font-mono text-xs py-8 text-center">Loading…</div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center" data-testid="requests-empty">
          <p className="text-ink-700 text-sm">{t("requests.emptyTitle")}</p>
          <p className="text-ink-500 text-xs mt-1">{t("requests.emptyHint")}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((r) => {
            const isOpen = openedId === r.id;
            const m = computeMatch(r, profile || {});
            return (
              <Card key={r.id} data-testid={`request-card-${r.id}`} onClick={() => navigate(`/app/requests/${r.id}`)} className={`transition cursor-pointer ${isOpen ? "border-electric-500/40" : "hover:border-electric-500/40"}`}>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {analyze ? (
                      <CompatibilityRing score={r.compatibility} size={62} stroke={5} />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-carbon-750 border border-carbon-600 flex flex-col items-center justify-center shrink-0">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-600">POSTED</span>
                        <span className="font-mono text-sm text-ink-900">{r.postedAt}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="font-mono text-[10px] text-ink-600">{r.id}</span>
                        <span className="text-[10px] text-ink-500">·</span>
                        <MatchBadge level={m.level} matched={[...m.matchedProcesses, ...m.matchedMaterials]} missing={[...m.missingProcesses, ...m.missingMaterials]} reason={m.explain} />
                        <span className="text-[10px] text-ink-600">{r.postedAt}</span>
                        {r.isNew && <LivePulse label={t("requests.new")} />}
                        {r.urgent && (
                          <Chip className="!bg-red-500/10 !text-red-300 !border-red-500/30 !text-[10px] !py-0.5">
                            {t("requests.urgent")}
                          </Chip>
                        )}
                      </div>
                      <h3 className="text-base font-medium text-ink-900 leading-snug">
                        {lang === "it" ? r.title : r.titleEn}
                      </h3>
                      <div className="text-xs text-ink-600 mt-1 flex items-center gap-2">
                        <span>{r.client}</span>
                        {r.reputation > 0 && <><span>·</span><ReputationBadge score={r.reputation} /></>}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {r.tags.map((tag) => <Chip key={tag}>{tag}</Chip>)}
                      </div>
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-0.5">{t("newRequest.material")}</div>
                          <div className="text-ink-800 truncate">{r.material || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-0.5">{t("requests.qty")}</div>
                          <div className="text-ink-800 font-mono">{r.quantity}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-0.5">{t("newRequest.budget")}</div>
                          <div className="text-ink-800 font-mono truncate">{r.budget}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mb-0.5">{t("newRequest.deadline")}</div>
                          <div className="text-ink-800 flex items-center gap-1"><Clock className="w-3 h-3" />{r.deadline || "—"}</div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[11px] text-ink-600 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {r.location || "—"}
                        </div>
                        <div className="flex items-center gap-3">
                          {analyze && (
                            <button onClick={(e) => { e.stopPropagation(); setOpenedId(isOpen ? null : r.id); }} data-testid={`why-match-${r.id}`} className="text-electric-400 text-xs flex items-center gap-1 hover:text-electric-600">
                              <Sparkles className="w-3 h-3" /> {t("requests.whyMatch")}
                            </button>
                          )}
                          <Button variant="primary" onClick={(e) => { e.stopPropagation(); navigate(`/app/requests/${r.id}`); }} data-testid={`respond-${r.id}`} className="!py-1.5 !px-3 !text-xs">
                            <Send className="w-3 h-3" /> {t("requests.respond")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {analyze && isOpen && (
                    <div className="mt-5 pt-5 border-t border-carbon-600 animate-fade-up" data-testid={`match-reasons-${r.id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-3.5 h-3.5 text-electric-400" />
                        <span className="text-[10px] font-mono uppercase tracking-widest text-electric-400">
                          {t("requests.whyMatch")}
                        </span>
                      </div>
                      <MatchScoreBreakdown
                        reasons={r.matchReasons.map((rr) => ({
                          score: rr.score,
                          label: t(`matchScore.${rr.label.toLowerCase()}`),
                        }))}
                      />
                      <p className="text-[11px] text-ink-600 mt-3 leading-snug">
                        {t("requests.compatibilityToggleHelp")}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
